import React, { useState } from 'react';
import { Mail, Send, CheckCircle2, Wrench, Lightbulb, ExternalLink, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from './SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONTACT_EMAIL = 'info@e-musclecars.com';

const EMuscleCar = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const openContact = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    });
    setSubmitted(false);
    setContactOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Wrap with a clear routing tag so the recipient instantly knows this
    // inquiry came from the E-Muscle Car page (routed via Shire's SMTP).
    const wrappedMessage = `[E-Muscle Car inquiry — please forward to ${CONTACT_EMAIL}]\n\n${form.message.trim()}`;
    try {
      await axios.post(`${API}/contact/submit`, { ...form, message: wrappedMessage, source: 'e-musclecars' });
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch soon.");
      setTimeout(() => setContactOpen(false), 1800);
    } catch (err) {
      toast.error('Could not send message. Please email us directly instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shireadvisors.com' },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'E-Muscle Car',
            item: 'https://shireadvisors.com/e-musclecars',
          },
        ],
      },
      {
        '@type': 'Organization',
        name: 'E-Muscle Cars',
        alternateName: 'E-Muscle Car',
        url: 'https://e-musclecars.com',
        email: CONTACT_EMAIL,
        description:
          'E-Muscle Cars electrified classic American muscle. Now focused on component sales and consulting for the EV conversion community.',
        sameAs: ['https://e-musclecars.com'],
        parentOrganization: {
          '@type': 'Organization',
          name: 'Shire Advisors, LLC',
          url: 'https://shireadvisors.com',
        },
      },
    ],
  };

  return (
    <div className="bg-neutral-950 text-neutral-100" data-testid="emuscle-page">
      <SEO
        title="E-Muscle Car — Component Sales & EV Conversion Consulting"
        description="E-Muscle Cars electrified classic American muscle. We've transitioned from full builds to focused component sales and EV conversion consulting. Contact info@e-musclecars.com."
        path="/e-musclecars"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative flex items-center overflow-hidden">
        {/* Background image — bg-contain + right-anchor so the full frame
            of the original photo shows on the right, revealing the whole
            vehicle rather than a cropped zoom-in. */}
        <div
          className="absolute inset-0 bg-contain bg-right bg-no-repeat"
          style={{ backgroundImage: "url('/e-musclecar/hero.jpg')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/85 to-neutral-950/40"
          aria-hidden="true"
        />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-neutral-950 to-transparent" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-16 sm:py-20 w-full">
          <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-4">
            The Next Chapter
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-4 max-w-3xl">
            The next chapter of{' '}
            <span className="text-amber-400 whitespace-nowrap">E-Muscle Cars</span>
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl leading-relaxed mb-8">
            After years of electrifying classic American muscle, we&apos;ve refocused. The
            garage doors are still open — just for a different kind of work.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={openContact}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
              data-testid="emuscle-hero-cta"
            >
              <Mail className="w-4 h-4 mr-2" />
              Get in Touch
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-neutral-600 bg-neutral-900/60 text-neutral-100 hover:bg-neutral-800 hover:text-white"
            >
              <a href="#offerings">What We Offer</a>
            </Button>
          </div>
          <p className="mt-8 text-xs text-neutral-500 italic tracking-wide">
            &ldquo;The Future Roars in Silence.&rdquo;
          </p>
        </div>
      </section>

      {/* Pivot narrative */}
      <section className="py-12 sm:py-16 border-t border-neutral-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-3">
            Where We&apos;re Going
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-6 leading-tight">
            From full builds to focused expertise.
          </h2>
          <div className="space-y-5 text-neutral-300 text-lg leading-relaxed">
            <p>
              E-Muscle Cars spent years engineering EV drivetrains into classic American
              icons — Mustangs, Camaros, Corvettes, Cobras, Broncos, and more. We built
              them serviceable, documented, and validated the way a modern OEM would.
            </p>
            <p>
              We&apos;re no longer taking on full end-to-end conversion builds. Instead, we&apos;re
              supporting the community two ways: <span className="text-amber-400 font-semibold">component sales </span>
              to builders who want the parts we spec&apos;d and trust, and{' '}
              <span className="text-amber-400 font-semibold">consulting</span> for shops
              and enthusiasts navigating their own conversion projects.
            </p>
            <p>
              Same engineering, same standards — a leaner scope so we can give it our full attention.
            </p>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section id="offerings" className="py-12 sm:py-16 border-t border-neutral-900 bg-neutral-900/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-3">
              What We Offer Today
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold">
              Two ways we can help.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Component Sales */}
            <article className="relative group bg-neutral-950 border border-neutral-800 rounded-xl p-8 transition-all hover:border-amber-500/60 hover:-translate-y-1 flex flex-col">
              <div className="absolute top-0 left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5">
                <Wrench className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-3 text-white">
                Component Sales
              </h3>
              <p className="text-neutral-400 leading-relaxed mb-5">
                Motors, inverters, chargers, DC-DC converters, cooling plates, contactors,
                instrumentation, and platform-specific mount kits — the same components
                we spec&apos;d for our own builds, available to shops and enthusiasts.
              </p>
              <ul className="text-sm text-neutral-500 space-y-1.5 mb-6 flex-1">
                <li>· Validated drivetrain components</li>
                <li>· Chassis-specific mounting hardware</li>
                <li>· Battery & controls systems</li>
                <li>· Phone support on installation questions</li>
              </ul>
              <Button
                asChild
                variant="outline"
                className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500 hover:text-neutral-950 hover:border-amber-500 bg-transparent mt-auto"
                data-testid="emuscle-components-cta"
              >
                <Link to="/e-musclecars/components-consulting#components">
                  See Component Catalog
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            </article>

            {/* Consulting */}
            <article className="relative group bg-neutral-950 border border-neutral-800 rounded-xl p-8 transition-all hover:border-amber-500/60 hover:-translate-y-1 flex flex-col">
              <div className="absolute top-0 left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5">
                <Lightbulb className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-3 text-white">
                Consulting
              </h3>
              <p className="text-neutral-400 leading-relaxed mb-5">
                Guidance for shops and builders taking on their own EV conversions. Draw
                on the lessons, drawings, and validated approaches from a decade of
                real-world platform work.
              </p>
              <ul className="text-sm text-neutral-500 space-y-1.5 mb-6 flex-1">
                <li>· Feasibility & platform selection</li>
                <li>· Drivetrain / battery specification</li>
                <li>· Thermal & charging system review</li>
                <li>· Build documentation & QA processes</li>
              </ul>
              <Button
                asChild
                variant="outline"
                className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500 hover:text-neutral-950 hover:border-amber-500 bg-transparent mt-auto"
                data-testid="emuscle-consulting-cta"
              >
                <Link to="/e-musclecars/components-consulting#consulting">
                  See Consulting Details
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            </article>
          </div>
        </div>
      </section>

      {/* Studio image + closing */}
      <section className="relative border-t border-neutral-900">
        <div className="absolute inset-0 bg-cover bg-center opacity-30"
             style={{ backgroundImage: "url('/e-musclecar/custom-conversion.png')" }}
             aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-950/70 to-neutral-950" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4 leading-tight">
            Still building the community.
          </h2>
          <p className="text-neutral-300 text-lg leading-relaxed mb-8">
            Whether you need a specific component, a second opinion on a design
            decision, or want to talk through a build, we&apos;re a message away.
          </p>
          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={openContact}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
              data-testid="emuscle-closing-cta"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact E-Muscle Cars
            </Button>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-sm text-neutral-400 hover:text-amber-400 transition-colors inline-flex items-center gap-1.5"
              data-testid="emuscle-mailto-link"
            >
              or email us directly at {CONTACT_EMAIL}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent
          className="max-w-lg w-[95vw] p-0 overflow-hidden bg-neutral-950 border-neutral-800 text-neutral-100"
          data-testid="emuscle-contact-modal"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-neutral-800 bg-neutral-900">
            <DialogTitle className="text-white font-serif text-xl pr-8">
              Contact E-Muscle Cars
            </DialogTitle>
            <DialogDescription className="text-amber-400 text-sm">
              Component sales & consulting inquiries
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="p-8 text-center" data-testid="emuscle-contact-success">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-serif font-bold text-white mb-1">
                Message Sent
              </h3>
              <p className="text-sm text-neutral-400">
                Thanks — we&apos;ll get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  required
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-form-name"
                />
                <Input
                  required
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-form-email"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-form-phone"
                />
                <Input
                  placeholder="Company / Shop (optional)"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-form-company"
                />
              </div>
              <Textarea
                required
                rows={5}
                placeholder="Tell us about your project — platform, components you&apos;re after, or the consulting question you have."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                data-testid="emuscle-form-message"
              />
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                Messages route through Shire Advisors and are forwarded to {CONTACT_EMAIL}.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-transparent border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                  onClick={() => setContactOpen(false)}
                  disabled={submitting}
                  data-testid="emuscle-form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
                  disabled={submitting}
                  data-testid="emuscle-form-submit"
                >
                  {submitting ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EMuscleCar;
